import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  IconButton,
  Button,
  Typography,
  Box,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { resolveApiUrl } from '../utils/api';

interface SubscriptionPlan {
  id: string;
  name: string;
  monthly_tgs_limit: number;
  ai_generation: boolean;
  manual_mode: boolean;
  export_formats: string[];
  custom_spacing: boolean;
  api_access: boolean;
  price_cents: number;
  price_display: string;
  features: string[];
}

interface SubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
}

export const SubscriptionDialog: React.FC<SubscriptionDialogProps> = ({ open, onClose }) => {
  const { authStatus, refreshAuthStatus, startTrial } = useAuth();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [trialLoading, setTrialLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [billingInterval, setBillingInterval] = useState<'month' | 'year'>('month');

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(resolveApiUrl('/api/subscription/plans'));
      setPlans(response.data.plans);
    } catch (err: any) {
      setError('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId: string) => {
    try {
      setLoading(true);
      
      if (planId === 'free') {
        // Cancel current subscription to downgrade to free
        await axios.post(resolveApiUrl('/api/subscription/cancel'));
        await refreshAuthStatus();
        onClose();
        return;
      }

      // Create checkout session on backend with billing interval
      const response = await axios.post(resolveApiUrl(`/api/subscription/checkout/${planId}`), {
        billing_interval: billingInterval,
        success_url: window.location.origin + '/subscription/success',
        cancel_url: window.location.origin + '/subscription/cancel'
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start upgrade process');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setTrialLoading(true);
      await startTrial(billingInterval);
      // Note: If startTrial succeeds, the page will redirect to Stripe
      // so we don't need to setTrialLoading(false) here
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start trial');
      setTrialLoading(false);
    }
  };

  const currentPlan = authStatus?.plan;

  // Calculate pricing info based on billing interval
  const pricingInfo = useMemo(() => {
    const monthlyPrice = 35; // $35 AUD/month
    const annualPrice = 294; // $294 AUD/year
    const annualSavings = (monthlyPrice * 12) - annualPrice; // $126 savings

    if (billingInterval === 'year') {
      return {
        display: `A$${annualPrice}`,
        period: 'year',
        monthly_equivalent: Math.round(annualPrice / 12),
        savings: annualSavings
      };
    }
    
    return {
      display: `A$${monthlyPrice}`,
      period: 'month',
      monthly_equivalent: monthlyPrice,
      savings: 0
    };
  }, [billingInterval]);

  const manualPlan = useMemo(() => {
    if (!plans || !plans.length) {
      // Fallback to a default manual plan if API fails
      return {
        id: 'manual_pro',
        name: 'Manual Pro',
        price_display: 'A$35',
        monthly_tgs_limit: 50,
        ai_generation: false,
        manual_mode: true,
        export_formats: ['PDF', 'PNG', 'JPEG'],
        custom_spacing: false,
        api_access: false,
        price_cents: 3500,
        features: ['Manual sign placement', 'Compliance check', 'Live chat support', 'Basic asset library']
      };
    }
    return (
      plans.find((plan) => plan.id === 'manual_pro') ||
      plans.find((plan) => plan.name.toLowerCase().includes('manual')) ||
      null
    );
  }, [plans]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      BackdropProps={{
        sx: {
          backdropFilter: 'none',
          bgcolor: 'rgba(6,6,10,0.94)',
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: '#0a0b0e',
          color: '#fff',
          borderRadius: 4,
          overflow: 'hidden',
          p: 0,
          width: 420,
        }
      }}
    >
      <Box position="relative" p={3}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            color: '#fff',
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ p: 0, textAlign: 'center' }}>
          {error && (
            <Typography color="error" mb={2}>{error}</Typography>
          )}

          <Stack spacing={3} alignItems="center">
            <Chip label="Manual" sx={{ bgcolor: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 600 }} />

            {/* Billing Interval Toggle */}
            <ToggleButtonGroup
              value={billingInterval}
              exclusive
              onChange={(_, newValue) => newValue && setBillingInterval(newValue)}
              sx={{
                bgcolor: 'rgba(255,255,255,0.05)',
                borderRadius: 2,
                '& .MuiToggleButton-root': {
                  color: 'rgba(255,255,255,0.6)',
                  border: 'none',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&.Mui-selected': {
                    bgcolor: '#e53935',
                    color: '#fff',
                    '&:hover': {
                      bgcolor: '#d32f2f',
                    }
                  }
                }
              }}
            >
              <ToggleButton value="month">Monthly</ToggleButton>
              <ToggleButton value="year">
                <Box display="flex" alignItems="center" gap={1}>
                  Annual
                  <Chip 
                    label="Save $126" 
                    size="small" 
                    sx={{ 
                      bgcolor: '#4caf50', 
                      color: '#fff',
                      height: 20,
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }} 
                  />
                </Box>
              </ToggleButton>
            </ToggleButtonGroup>

            <Box>
              <Typography variant="h3" fontWeight={700}>
                {pricingInfo.display}
              </Typography>
              <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">
                /{pricingInfo.period}
              </Typography>
              {billingInterval === 'year' && (
                <Typography variant="caption" color="rgba(76,175,80,0.9)" display="block" mt={0.5}>
                  A${pricingInfo.monthly_equivalent}/month when billed annually
                </Typography>
              )}
            </Box>

            <Typography variant="body1" color="rgba(255,255,255,0.75)" maxWidth={280}>
              Start your 7-day free trial. {billingInterval === 'year' ? 'Billed annually' : 'Billed monthly'} after trial ends.
            </Typography>

            <Button
              fullWidth
              size="large"
              variant="contained"
              sx={{
                bgcolor: '#e53935',
                ':hover': { 
                  bgcolor: '#d32f2f' 
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(229, 57, 53, 0.6)',
                  color: '#fff',
                  opacity: 1,
                },
                borderRadius: 2,
                py: 1.5,
                fontWeight: 600,
              }}
              onClick={handleStartTrial}
              disabled={trialLoading || loading}
            >
              {trialLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
              Try for free
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                </Box>
              ) : (
                'Try for free'
              )}
            </Button>

            <Box width="100%">
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.15)' }} />
                <Typography
                  variant="subtitle2"
                  color="rgba(255,255,255,0.6)"
                  sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 1.5,
                  }}
                >
                  Features
                </Typography>
                <Box sx={{ flex: 1, height: '1px', bgcolor: 'rgba(255,255,255,0.15)' }} />
              </Box>

              <List dense sx={{ color: '#fff', px: 1 }}>
                {[
                  'Manual sign placement',
                  'Customize Signs',
                  'Compliance check',
                  'Live chat support',
                  'Basic asset library'
                ].map((feature) => (
                  <ListItem key={feature} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckIcon fontSize="small" sx={{ color: '#4caf50' }} />
                    </ListItemIcon>
                    <ListItemText primary={feature} primaryTypographyProps={{ fontSize: '0.95rem' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        </DialogContent>
      </Box>
    </Dialog>
  );
};
